import React, { Suspense, useEffect, useState } from "react";
import { Link } from 'react-router-dom';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import { Tab } from '@mui/material';
import { Box } from '@mui/material';
import { Grid } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import CatalogAPI from "api/faber/CatalogAPI";
import ColorBox from 'common/Box/ColorWhiteBox';
import PageLayoutV2 from 'common/Layout/Page/PageLayoutV2';
import LayoutSkeleton from "common/Skeletons/LayoutSkeleton";
import NormalText from "common/Typography/NormalText";
import OrbitSession from "hooks/useCookies";

import AddCatalogDialogV2 from "./components/AddCatalogDialogV2";
import AddPackageDialog from "./components/AddPackageDialog";
import AddPageDialog from "./components/AddPageDialog";
import CatalogPriceTab from "./components/CatalogPriceTab";
import ManagePageAddCatalog from "./components/ManagePageAddCatalog";
import ManagePageCatalog from "./components/ManagePageCatalog";
function FaberCatalogueDashboardV2() {
  const faberToken = OrbitSession.get("faberToken");
  const [catalogRows, setCatalogRows] = useState<GridRowDataType[]>([]);
  const [selectedCatalogIds, setSelectedCatalogIds] = useState<string[]>([]);
  interface GridRowDataType {
    id: string;
    description: string[];
    name: string;
    dataInGB: number;
    packageType: string;
    tenant: string;
    validityInDays: number;
    currency: string;
    value: string;
    price?: {
      currency: string;
      value: string;
    }
  }

  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: "Name",
      width: 200,
      filterable: true,
    },
    { field: "dataInGB", headerName: "Data In GB", width: 100 },
    {
      field: "packageType",
      headerName: "Package Type",
      width: 150,
    },
    { field: "tenant", headerName: "Tenant", width: 100 },
    { field: "validityInDays", headerName: "Validity In Days", width: 100 },
    {
      field: "price.currency",
      headerName: "Currency",
      width: 100,
      valueGetter: (params) => params.row.price?.currency,
    },
    {
      field: "price.value",
      headerName: "Value",
      width: 100,
      valueGetter: (params) => params.row.price?.value,
    },
    { field: "description", headerName: "Description", width: 475 },
  ];

  interface GridRowPageDataType {
    id: string;
    name: string;
    displayName: string;
    destinationType: string;
    createdAt: string;
  }
  const pageColumns: GridColDef[] = [
    {
      field: "displayName",
      headerName: "Display Name",
      width: 300,
      filterable: true,
      sortable: true,
    },
    {
      field: "name",
      headerName: "Page Name",
      width: 300,
      filterable: true,
    },
    { field: "destinationType", headerName: "Destination Type", width: 250 },

    {
      field: "updatePage",
      flex: 0.75,
      width: 200,
      filterable: false,
      sortable: false,
      headerName: "Catalog info",
      renderCell: (params: { row: GridRowPageDataType }) => {
        const { row } = params;
        return <ManagePageCatalog id={row.id.toString()} />;
      },
    },
    {
      field: "addCatalog",
      flex: 0.75,
      width: 200,
      filterable: false,
      sortable: false,
      headerName: "Add Catalog",
      renderCell: (params: GridRenderCellParams<GridRowPageDataType>) => {
        const { row } = params;
        return <ManagePageAddCatalog id={row.id.toString()} />;
      },
    },
  ];

  const { data: catalogItems } = useQuery(["getCatalogItems"], async () => {
    return CatalogAPI.getAllCatalogItems(faberToken);
  });

  useEffect(() => {
    if (catalogItems) {
      setCatalogRows(catalogItems);
    }
  }, [catalogItems]);

  const [catalogPages, setCatalogPages] = useState([])

  const { data: pages } = useQuery(["getCatalogPages"], async () => {
    return CatalogAPI.getAllCatalogPages(faberToken);
  })

  useEffect(() => {
    if (pages && pages.data) {
      setCatalogPages(pages.data)
    }
  }, [pages]);

  const [tabValue, setTabValue] = useState('1')
  const handleTabChange = (event, newNum) => setTabValue(newNum)

  return (
    <Suspense fallback={<LayoutSkeleton />}>
      <TabContext value={tabValue}>
        <PageLayoutV2 header="Manage Catalog">
          <ColorBox sx={{ minWidth: "1000px" }}>
            <TabList
              onChange={handleTabChange}
              aria-label='lab API tabs example'
            >
              <Tab label='Catalog' value='1' component={Link} to='#campaign' />
              <Tab
                label='Pages'
                value='2'
                component={Link}
                to='#catalogs'
              />
              <Tab
                label='Price'
                value='3'
                component={Link}
                to='#catalogprices'
              />
            </TabList>

            <TabPanel value='1'>
              <ColorBox>
                <Box display="flex" flexDirection="row" gap={2}>
                  <AddPackageDialog />
                  <AddCatalogDialogV2 />
                </Box>
                <Box sx={{ minWidth: "1000px" }}>
                  <NormalText isBold>Catalog Items</NormalText>
                  <Grid item xs={12} height={640}>
                    <DataGrid
                      columns={columns}
                      rows={catalogRows}
                      getRowId={(row) => row.id + row.price?.currency + row.price?.value}
                      rowsPerPageOptions={[10]}
                      pageSize={10}
                    />
                  </Grid>
                </Box>
              </ColorBox>
            </TabPanel>
            <TabPanel value='2'>
              <ColorBox>
                <Box display="flex" flexDirection="row" gap={2}>
                  <AddPageDialog />
                </Box>
                <NormalText isBold>Catalog Pages</NormalText>
                <Grid item xs={12} height={640}>
                  <DataGrid
                    columns={pageColumns}
                    rows={catalogPages}
                    getRowId={(row) => row.id}
                    onRowClick={(row) => console.log(row)}
                    rowsPerPageOptions={[10]}
                    pageSize={10}
                  />
                </Grid>
              </ColorBox>
            </TabPanel>
            <TabPanel value="3">
              <ColorBox>
                <CatalogPriceTab
                  faberToken={faberToken}
                  catalogRows={catalogRows}
                  selectedCatalogIds={selectedCatalogIds}
                  setSelectedCatalogIds={setSelectedCatalogIds}
                />
              </ColorBox>
            </TabPanel>
          </ColorBox>
        </PageLayoutV2>
      </TabContext>
    </Suspense>
  );
}

export default FaberCatalogueDashboardV2;